import React, { useMemo, useState } from 'react';
import type { FC, ReactNode } from 'react';
import PropTypes from 'prop-types';
import { Hidden, makeStyles, ThemeProvider } from '@material-ui/core';
import { createTheme } from 'src/theme';
import type { Theme } from 'src/theme';
import { THEMES } from 'src/constants';
import useSettings from 'src/hooks/useSettings';
import { useLocation } from 'react-router-dom';
import IdleTimeoutHandler from 'src/components/IdleTimeoutHandler';
import ErrorBoundary from 'src/components/ErrorBoundary';
import NavBar from './NavBar';
import TopBar from './TopBar';

interface DashboardLayoutProps {
  children?: ReactNode
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    backgroundColor: theme.palette.background.dark,
    display: 'flex',
    height: '100%',
    overflow: 'hidden',
    width: '100%'
  },
  wrapper: {
    display: 'flex',
    flex: '1 1 auto',
    overflow: 'hidden',
    // The top bar only exists below lg, to open the drawer.
    paddingTop: 64,
    [theme.breakpoints.up('lg')]: {
      paddingTop: 0,
      paddingLeft: 256
    }
  },
  contentContainer: {
    display: 'flex',
    flex: '1 1 auto',
    overflow: 'hidden'
  },
  content: {
    flex: '1 1 auto',
    height: '100%',
    overflow: 'auto'
  }
}));

const DashboardLayout: FC<DashboardLayoutProps> = ({ children }) => {
  const classes = useStyles();
  const location = useLocation();
  const [isMobileNavOpen, setMobileNavOpen] = useState<boolean>(false);
  const { settings } = useSettings();
  // The sidebar is dark in both themes, so it always gets the dark palette.
  const navTheme = useMemo(() => {
    const theme = createTheme({ ...settings, theme: THEMES.ONE_DARK });
    // Next to the light canvas the dark theme's near-black reads too heavy,
    // so light mode gets a softer slate (desktop drawer + mobile drawer paper).
    if (settings.theme === THEMES.LIGHT) {
      theme.palette.background.default = '#1A2B34';
      theme.palette.background.paper = '#1A2B34';
    }
    return theme;
  }, [settings]);

  return (
    <div className={classes.root}>
      {/* Each piece of the shell fails on its own: a crash in the top bar or
          sidebar hides that piece (logged) instead of blanking the app. */}
      <ErrorBoundary fallback={null}>
        <IdleTimeoutHandler />
      </ErrorBoundary>
      <ErrorBoundary fallback={null}>
        <Hidden lgUp>
          <TopBar onMobileNavOpen={() => setMobileNavOpen(true)} />
        </Hidden>
      </ErrorBoundary>
      <ErrorBoundary fallback={null}>
        <ThemeProvider theme={navTheme}>
          <NavBar
            onMobileClose={() => setMobileNavOpen(false)}
            openMobile={isMobileNavOpen}
          />
        </ThemeProvider>
      </ErrorBoundary>
      <div className={classes.wrapper}>
        <div className={classes.contentContainer}>
          <div className={classes.content}>
            {/* A crashing page shows a recovery screen here while the sidebar
                and top bar keep working; changing page resets it. */}
            <ErrorBoundary resetKey={location.pathname}>{children}</ErrorBoundary>
          </div>
        </div>
      </div>
    </div>
  );
};

DashboardLayout.propTypes = {
  children: PropTypes.node
};

export default DashboardLayout;
